import React, { useState } from 'react';

/**
 * Enhanced confirm/alert modal.
 *
 * Props:
 *   isOpen       - bool
 *   title        - string
 *   message      - string | ReactNode
 *   onConfirm    - fn (can be async — modal shows "Processing..." until resolved)
 *   onCancel     - fn
 *   variant      - 'danger' | 'warning' | 'info'   (default 'danger')
 *   confirmLabel - string (default 'Delete')
 *   cancelLabel  - string (default 'Cancel')
 */
export default function ConfirmModal({
  isOpen, title, message, onConfirm, onCancel,
  variant = 'danger', confirmLabel = 'Delete', cancelLabel = 'Cancel',
}) {
  const [busy, setBusy] = useState(false);
  if (!isOpen) return null;

  const palette = {
    danger:  { color: '#dc2626', icon: '🗑️' },
    warning: { color: '#d97706', icon: '⚠️'  },
    info:    { color: '#2563eb', icon: 'ℹ️'   },
  }[variant] || { color: '#dc2626', icon: '🗑️' };

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try { await onConfirm?.(); }
    finally { setBusy(false); }
  };

  const handleBackdrop = (e) => {
    // Click outside modal body closes, unless busy
    if (e.target === e.currentTarget && !busy) onCancel?.();
  };

  return (
    <div
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fade-in 0.12s ease-out',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, padding: '24px 28px',
        maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        animation: 'pop-in 0.15s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>{palette.icon}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a202c' }}>
            {title || 'Please confirm'}
          </h3>
        </div>
        <div style={{ margin: '0 0 20px', color: '#4a5568', fontSize: 13.5, lineHeight: 1.6 }}>
          {message || 'Are you sure? This action cannot be undone.'}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '8px 18px', borderRadius: 6, border: '1px solid #cbd5e0',
              background: '#f7fafc', cursor: busy ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13, opacity: busy ? 0.5 : 1,
            }}>
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none',
              background: palette.color, color: '#fff',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13, opacity: busy ? 0.7 : 1,
              minWidth: 88,
            }}>
            {busy ? '⏳ ...' : confirmLabel}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pop-in  { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}
